import type {
  ScheduleFromTakeoffFloor,
  ScheduleFromTakeoffItem,
  ScheduleFromTakeoffResult,
  ScheduleTakeoffRefreshResult,
} from '@obraplan/core/application/contracts/schedule-from-takeoff';
import {
  formatDecimal,
  formatItemQuantity,
  timelineOf,
  type Timeline,
} from './schedule-items-model';

/**
 * A conta de exibição do "Gerar do quantitativo" e da revisão (Fase 31).
 *
 * Como em `schedule-items-model.ts`, nada aqui decide regra: a proposta, a ordem e a equipe
 * vêm prontas do servidor (RN-415 a RN-417). Aqui só se move um pavimento na lista, se lê o
 * número digitado da equipe e se agrupa o que vai para a tela.
 */

/** A equipe aceita pelo servidor: de 1 a 1.000 pessoas, inteira. */
export const MAX_CREW = 1000;

/**
 * O número de pessoas digitado, ou `undefined` quando não vale — vazio, fração, zero ou
 * acima do limite. A vírgula é aceita: "3,0" é três pessoas.
 */
export function parseCrew(text: string): number | undefined {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 1 && value <= MAX_CREW ? value : undefined;
}

/**
 * A ordem dos pavimentos na tela: a escolhida (ou a que o servidor usou), sem o pavimento que
 * não existe mais, e com o que faltar no fim, na ordem padrão.
 */
export function floorsInOrder(
  floors: readonly ScheduleFromTakeoffFloor[],
  order: readonly string[],
): ScheduleFromTakeoffFloor[] {
  const byId = new Map(floors.map((floor) => [floor.floorId, floor]));
  const listed = order.flatMap((id) => {
    const floor = byId.get(id);
    return floor === undefined ? [] : [floor];
  });
  const seen = new Set(listed.map((floor) => floor.floorId));
  return [...listed, ...floors.filter((floor) => !seen.has(floor.floorId))];
}

/** Sobe (`-1`) ou desce (`+1`) um pavimento uma posição. Na ponta, a lista fica como está. */
export function moveFloor(
  order: readonly string[],
  floorId: string,
  direction: -1 | 1,
): readonly string[] {
  const index = order.indexOf(floorId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= order.length) return order;
  const next = [...order];
  next[index] = order[target]!;
  next[target] = floorId;
  return next;
}

/** Um pavimento da prévia com os itens dele, na ordem da linha de balanço. */
export interface PreviewFloorGroup {
  readonly floorId: string;
  readonly floorName: string;
  readonly towerName: string;
  readonly items: readonly ScheduleFromTakeoffItem[];
}

/**
 * Os itens da prévia agrupados pelo pavimento, na ordem em que o servidor os entrega: o
 * pavimento aparece onde o primeiro item dele aparece.
 */
export function groupPreviewByFloor(
  items: readonly ScheduleFromTakeoffItem[],
): PreviewFloorGroup[] {
  const groups = new Map<
    string,
    { floorName: string; towerName: string; items: ScheduleFromTakeoffItem[] }
  >();
  for (const item of items) {
    const group = groups.get(item.floorId);
    if (group === undefined) {
      groups.set(item.floorId, {
        floorName: item.floorName,
        towerName: item.towerName,
        items: [item],
      });
    } else {
      group.items.push(item);
    }
  }
  return [...groups.entries()].map(([floorId, group]) => ({ floorId, ...group }));
}

/** A escala das barras da prévia: do mês do primeiro início ao do último fim. */
export function previewTimeline(items: readonly ScheduleFromTakeoffItem[]): Timeline | undefined {
  return timelineOf(
    items.map((item) => ({
      plannedStart: item.start,
      plannedEnd: item.end,
      baselineStart: item.start,
      baselineEnd: item.end,
    })),
  );
}

/** A diferença com sinal: "+12,5" ou "−3". Zero não tem sinal. */
export function signed(value: number, maxDigits = 2): string {
  const text = formatDecimal(Math.abs(value), maxDigits);
  if (value > 0) return `+${text}`;
  if (value < 0) return `−${text}`;
  return text;
}

/** "1.234,5 m²", ou "—" sem número. */
export function quantityText(quantity: number | null, unit: string | null): string {
  return formatItemQuantity({ quantity, unit }) ?? '—';
}

/** "80 HH", ou "—" sem número. */
export function hoursText(laborHours: number | null): string {
  return laborHours === null ? '—' : `${formatDecimal(laborHours)} HH`;
}

/** "1 item" / "3 itens" — e o mesmo para as outras palavras da tela. */
export function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/** "a", "a e b", "a, b e c". */
export function byExtension(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1] ?? ''}`;
}

// ── Os avisos de quem confirma ─────────────────────────────────────────────────

/** "12 itens criados, 3 atualizados e 1 sem mudança." — e as frentes, quando houve. */
export function resultadoDaGeracao(result: ScheduleFromTakeoffResult): string {
  const frentes =
    result.createdFronts > 0
      ? ` ${plural(result.createdFronts, 'frente criada', 'frentes criadas')}.`
      : '';
  return `Cronograma gerado do quantitativo: ${plural(result.created, 'item criado', 'itens criados')}, ${plural(result.updated, 'atualizado', 'atualizados')} e ${String(result.unchanged)} sem mudança.${frentes}`;
}

/** "Quantitativo aplicado: 3 itens atualizados, 1 voltou e 2 marcados como fora." */
export function resultadoDaRevisao(result: ScheduleTakeoffRefreshResult): string {
  const partes = [plural(result.updated, 'item atualizado', 'itens atualizados')];
  if (result.returned > 0) partes.push(plural(result.returned, 'voltou', 'voltaram'));
  if (result.markedGone > 0) {
    partes.push(
      plural(
        result.markedGone,
        'marcado como fora do quantitativo',
        'marcados como fora do quantitativo',
      ),
    );
  }
  return `Quantitativo aplicado: ${byExtension(partes)}. As datas e o avanço não mudaram.`;
}
